import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Despatch } from "../../../../../../models/despatch";
import { Filters, Searches } from "../../../../../../models/shipeezi.table";
import DespatchProductService from "../../../../../../services/despatch.product.service";
import DespatchService from "../../../../../../services/despatch.service";
import { LocationService } from "../../../../../../services/location.service";
import { checkError } from "../../../../../../util/checkError";
import { TypeError } from "../../../../../../util/enums";
import { DespatchKeys, LocationsKeys } from "../../../../../../util/queries";
import { useDespatchContext } from "../../context/DespatchContext";
import useRoutePlannerAction from "../../hooks/useRoutePlannerActions";
import { DespatchChangeKeys } from "../types";

interface SaveDespatchProps {
  id?: number;
  despatch: Despatch;
  overrideChangedOptions?: DespatchChangeKeys;
  uuid?: string;
}

const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const useAction = () => {
  const { currentUser } = useAppState();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();
  const [requestingSaveDespatch, setRequestingSaveDespatch] = useState(false);
  const { changedOptions } = useDespatchContext();

  const { useUpdateToRoutePlanner } = useRoutePlannerAction();

  const { mutate: updateInRoutePlanner } = useUpdateToRoutePlanner();

  const getDespatchById = (DespatchId: number, callerCallback?: (DespatchId: number) => void) => {
    if (DespatchId && callerCallback) callerCallback(DespatchId);

    return useQuery({
      queryKey: DespatchKeys.getById(DespatchId),
      queryFn: async () => {
        const response = await DespatchService.getById(Number(DespatchId), {
          include: JSON.stringify([
            { association: "originAddress" },
            { association: "destinationAddress" },
            { association: "shippingItems" },
            { association: "fleets" },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!DespatchId,
      cacheTime: CACHE_TIME,
    });
  };

  const getProductsByDespatch = (id: number) => {
    return useQuery({
      queryKey: DespatchKeys.getProducts(id),
      queryFn: async () => {
        const response = await DespatchProductService.getAll({
          despatchId: id,
          include: JSON.stringify([
            {
              association: "container",
              include: [{ association: "product" }],
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!id,
      cacheTime: CACHE_TIME,
    });
  };

  const getAvailableProducts = (
    page: number,
    pageSize: number = 10,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: DespatchKeys.getAvailableProducts([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await DespatchService.getAvailableProducts({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            {
              association: "product",
            },
            {
              association: "warehouse",
            },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      staleTime: 2 * 60,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllLocations = (
    page: number = 1,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: LocationsKeys.getAll([page, pageSize, searches, filters, currentUser?.companyId]),
      queryFn: async () => {
        const response = await LocationService.getAll({
          companyId: currentUser?.companyId,
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getStatuses = () => {
    return useQuery({
      queryKey: DespatchKeys.getStatuses(),
      queryFn: async () => {
        const response = await DespatchService.getStatuses();

        if (response && response.data) return response.data;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const countLocations = (searches?: Searches, filters?: Filters) => {
    return useQuery({
      queryKey: LocationsKeys.count([searches, filters, currentUser?.companyId]),
      queryFn: async () => {
        const response = await LocationService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useSaveDespatch = (callback?: () => void) => {
    return useMutation({
      mutationKey: DespatchKeys.save(),
      mutationFn: ({ id, despatch, overrideChangedOptions }: SaveDespatchProps) => {
        if (id)
          return DespatchService.update(id, {
            record: despatch,
            changedOptions: overrideChangedOptions || changedOptions,
          });

        return DespatchService.create(
          { ...despatch, companyId: currentUser?.companyId }
          // TODO: uncoment when there is uuid on despatch backend
          //   {
          //   record: {
          //     ...despatch,
          //     companyId: currentUser?.companyId,
          //   },
          //   uuid,
          // }
        );
      },
      retry: 0,
      networkMode: "online",
      onMutate: () => setRequestingSaveDespatch(true),
      onError: (error: AxiosError<Error>) => {
        setRequestingSaveDespatch(false);
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: (response, variables) => {
        const requestWasBlocked = response?.data?.type === TypeError.ERROR_WITHOUT_TOAST;

        if (requestWasBlocked) return;
        setRequestingSaveDespatch(false);

        if (
          variables?.id &&
          currentUser?.company.useRoutePlanner &&
          variables?.despatch.externalUuid
        ) {
          updateInRoutePlanner({
            id: variables?.despatch.externalUuid,
            despatch: variables.despatch,
          });
        }

        queryClient.invalidateQueries({ queryKey: [DespatchKeys.all] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
        history.push("/app/despatches");
      },
    });
  };

  return {
    requestingSaveDespatch,
    countLocations,
    getDespatchById,
    getProductsByDespatch,
    getAvailableProducts,
    getAllLocations,
    getStatuses,
    useSaveDespatch,
  };
};

export default useAction;
